import _ from 'lodash';

import {
  CHANNEL_EMPTY_VALUE,
  ChannelStatus,
  DEFAULT_PATH_STEP_ORDER,
  INTAKE_METHOD_OPTIONS,
  LOCK_EXTENSIONS_NOT_SET,
  PathStep,
} from './Channels.constants';

const PATH_STEP_VALUES = Object.values(PathStep);

export const getFieldName = (name, { lockDeskSettingId, lockExtensionId }) =>
  [name, lockDeskSettingId, lockExtensionId].filter(Boolean).join('');

/**
 * Sanitize a persisted `stepOrder` string into a full, ordered list of every PathStep
 * key — null/empty, unknown keys, and duplicates are all tolerated. Unknown keys are
 * dropped and any missing keys are appended in the default order, so the result always
 * contains all five steps exactly once.
 *
 * @param {string|null|undefined} stepOrder - comma-separated PathStep keys, or null/undefined.
 * @returns {string[]} every PathStep key, ordered.
 */
export const parsePathStepOrder = stepOrder => {
  const validKeys = (stepOrder ?? '')
    .split(',')
    .map(key => key.trim())
    .filter(key => PATH_STEP_VALUES.includes(key));

  const dedupedKeys = [...new Set(validKeys)];
  const missingKeys = DEFAULT_PATH_STEP_ORDER.filter(key => !dedupedKeys.includes(key));

  return [...dedupedKeys, ...missingKeys];
};

/**
 * Serialize an ordered list of PathStep keys back into the comma-separated string the
 * backend expects.
 *
 * @param {string[]|null|undefined} stepOrder
 * @returns {string}
 */
export const serializePathStepOrder = stepOrder => (stepOrder ?? []).join(',');

export const getChannelStatus = ({ channelId, deskChannels }) => {
  const deskChannel = (deskChannels ?? []).find(({ id }) => id === channelId);

  if (!deskChannel) {
    return null;
  }

  return deskChannel.hasPublishedSheet ? ChannelStatus.LIVE : ChannelStatus.NO_SHEET;
};

export const getIntakeLabel = intakeMethod =>
  INTAKE_METHOD_OPTIONS.find(({ value }) => value === intakeMethod)?.label ?? CHANNEL_EMPTY_VALUE;

export const summarizeLockExtensions = lockDeskSettings => {
  const settings = lockDeskSettings ?? [];
  const maxDays = Math.max(
    0,
    ...settings.map(({ maxExtensionPeriodDays }) => maxExtensionPeriodDays ?? 0),
  );
  const optionCount = settings.reduce(
    (count, { lockExtensions }) => count + (lockExtensions?.length ?? 0),
    0,
  );

  if (!maxDays && !optionCount) {
    return LOCK_EXTENSIONS_NOT_SET;
  }

  const optionsLabel = `${optionCount} option${optionCount === 1 ? '' : 's'}`;

  return maxDays ? `Up to ${maxDays} days · ${optionsLabel}` : optionsLabel;
};

export const countOfferedPrograms = ({ programIds, programsById }) =>
  _.isNil(programIds)
    ? null
    : programIds.filter(programId => programsById[programId] && !programsById[programId].isDeleted)
        .length;
